import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { emitter } from '../events/bridge-events-emitter';

/* eslint-disable no-console */

/**
 * Configuration options for the debug consumer.
 */

/**
 * Default debug configuration.
 */
var DEFAULT_CONFIG = {
  enabled: false,
  maxHistory: 1000
};

/**
 * Debug consumer class that formats and displays bridge API events.
 */
export var BridgeDebugConsumer = /*#__PURE__*/function () {
  function BridgeDebugConsumer() {
    _classCallCheck(this, BridgeDebugConsumer);
    _defineProperty(this, "config", _objectSpread({}, DEFAULT_CONFIG));
    _defineProperty(this, "subscription", null);
    _defineProperty(this, "eventHistory", []);
    _defineProperty(this, "clientColors", new Map());
    _defineProperty(this, "commandColors", new Map());
    _defineProperty(this, "recentErrors", new Map());
    _defineProperty(this, "ERROR_DEBOUNCE_MS", 5000);
    // 5 seconds
    /**
     * Color palette for consistent coloring.
     */
    _defineProperty(this, "colors", ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9']);
    /**
     * Event type icons for visual distinction.
     */
    _defineProperty(this, "eventIcons", {
      // Bridge events
      'bridge-ready': '🌉',
      'bridge-destroyed': '💥',
      // Client events
      'client-registered': '📝',
      'client-ready': '✅',
      'client-discovered': '🔍',
      'client-teardown': '🔄',
      // Command events
      'command-created': '⚡',
      'command-sent': '📤',
      'command-received': '📥',
      'command-completed': '✅',
      'command-failed': '❌',
      // Queue events
      'message-queued': '📋',
      'queue-flushed': '🚀',
      'queue-cleared': '🧹',
      'message-dropped': '🗑️',
      // Transport events
      'transport-ready': '🚛',
      'transport-message-sent': '📡',
      'transport-message-received': '📻',
      'transport-error': '⚠️',
      'transport-destroyed': '💀',
      // Handler events
      'handler-registered': '🔧',
      'handler-unregistered': '🔓',
      // Broadcast events
      'command-broadcast': '📢'
    });
  }
  return _createClass(BridgeDebugConsumer, [{
    key: "enable",
    value:
    /**
     * Start consuming and logging bridge API events.
     */
    function enable() {
      var _this = this;
      if (this.subscription) {
        return; // Already enabled
      }
      this.config.enabled = true;
      this.subscription = emitter().onAny(function (event) {
        if (_this.config.enabled && _this.shouldLogEvent(event)) {
          _this.logEvent(event);
          _this.addToHistory(event);
        }
      });
      console.log('%c🌉 Rovo Bridge API Debug Mode Enabled', 'color: #4ECDC4; font-weight: bold; font-size: 14px;');
    }

    /**
     * Stop consuming bridge API events.
     */
  }, {
    key: "disable",
    value: function disable() {
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
      this.config.enabled = false;
      console.log('%c🌉 Rovo Bridge API Debug Mode Disabled', 'color: #FF6B6B; font-weight: bold; font-size: 14px;');
    }

    /**
     * Update debug configuration.
     */
  }, {
    key: "configure",
    value: function configure(newConfig) {
      this.config = _objectSpread(_objectSpread({}, this.config), newConfig);
      console.log('%c🔧 Debug configuration updated', 'color: #96CEB4; font-weight: bold;', this.config);
    }

    /**
     * Get current configuration.
     */
  }, {
    key: "getConfig",
    value: function getConfig() {
      return _objectSpread({}, this.config);
    }

    /**
     * Get event history.
     */
  }, {
    key: "getHistory",
    value: function getHistory() {
      return _toConsumableArray(this.eventHistory);
    }

    /**
     * Clear event history.
     */
  }, {
    key: "clearHistory",
    value: function clearHistory() {
      this.eventHistory = [];
      console.log('%c🧹 Event history cleared', 'color: #FFEAA7; font-weight: bold;');
    }

    /**
     * Get debug statistics.
     */
  }, {
    key: "getStats",
    value: function getStats() {
      var eventCounts = this.eventHistory.reduce(function (counts, event) {
        counts[event.type] = (counts[event.type] || 0) + 1;
        return counts;
      }, {});
      return {
        enabled: this.config.enabled,
        totalEvents: this.eventHistory.length,
        eventCounts: eventCounts,
        uniqueClients: new Set(this.eventHistory.filter(function (e) {
          return 'clientId' in e;
        }).map(function (e) {
          return e.clientId;
        })).size,
        uniqueCommands: new Set(this.eventHistory.filter(function (e) {
          return 'commandType' in e;
        }).map(function (e) {
          return e.commandType;
        })).size
      };
    }

    /**
     * Export event history as JSON.
     */
  }, {
    key: "exportHistory",
    value: function exportHistory() {
      return JSON.stringify({
        exportedAt: new Date().toISOString(),
        config: this.config,
        events: this.eventHistory,
        stats: this.getStats()
      }, null, 2);
    }

    /**
     * Log an event to the console with simple formatting.
     */
  }, {
    key: "logEvent",
    value: function logEvent(event) {
      var icon = this.eventIcons[event.type] || '📋';
      var timestamp = this.formatTimestamp(event.timestamp);
      var bridgeId = event.bridgeId;

      // Create collapsible group with basic info
      var groupTitle = "".concat(icon, " ").concat(timestamp, " ").concat(event.type, " [").concat(bridgeId, "]");
      console.groupCollapsed("%c".concat(groupTitle), this.getEventTypeStyle(event.type));

      // Add key details in a simple format
      this.logKeyDetails(event);

      // Log raw event data
      console.log('%cRaw Event:', 'color: #888; font-style: italic;', event);
      console.groupEnd();
    }

    /**
     * Log key event details in a simple format.
     */
  }, {
    key: "logKeyDetails",
    value: function logKeyDetails(event) {
      // Client ID (if present)
      if ('clientId' in event) {
        console.log("%c\uD83D\uDC64 Client: %c".concat(event.clientId), 'font-weight: bold;', this.getClientStyle(event.clientId));
      }

      // Command details (if present)
      if ('commandType' in event) {
        var cmdEvent = event;
        console.log("%c\u26A1 Command: %c".concat(cmdEvent.commandType), 'font-weight: bold;', this.getCommandStyle(cmdEvent.commandType));
        if (cmdEvent.senderId && cmdEvent.receiverId) {
          console.log("%c\uD83D\uDCE4 %c".concat(cmdEvent.senderId, " %c\u2192 %c\uD83D\uDCE5 %c").concat(cmdEvent.receiverId), 'font-weight: bold;', this.getClientStyle(cmdEvent.senderId), 'color: #888;', 'font-weight: bold;', this.getClientStyle(cmdEvent.receiverId));
        }
        if (cmdEvent.duration !== undefined) {
          console.log("%c\u23F1\uFE0F Duration: %c".concat(this.formatDuration(cmdEvent.duration)), 'font-weight: bold;', this.getDurationStyle(cmdEvent.duration));
        }
      }

      // Error (if present)
      if ('error' in event && event.error) {
        console.log('%c❌ Error:', 'color: #FF6B6B; font-weight: bold;', event.error);
      }

      // Queue size (if present)
      if ('queueSize' in event) {
        console.log("%c\uD83D\uDCCB Queue Size: %c".concat(event.queueSize), 'font-weight: bold;', this.getQueueSizeStyle(event.queueSize));
      }
    }

    /**
     * Check if an event should be logged (with debouncing for transport errors).
     */
  }, {
    key: "shouldLogEvent",
    value: function shouldLogEvent(event) {
      // Apply event filter if configured
      if (this.config.eventFilter && this.config.eventFilter.length > 0) {
        var matchesFilter = this.config.eventFilter.some(function (filter) {
          // Support category filters
          if (filter === 'commands') {
            return event.type.startsWith('command-');
          }
          if (filter === 'clients') {
            return event.type.startsWith('client-');
          }
          if (filter === 'transport') {
            return event.type.startsWith('transport-');
          }
          if (filter === 'queue') {
            return event.type.includes('queue') || event.type.includes('message-');
          }
          if (filter === 'handlers') {
            return event.type.startsWith('handler-');
          }
          if (filter === 'bridge') {
            return event.type.startsWith('bridge-');
          }
          if (filter === 'errors') {
            return event.type.includes('error') || event.type.includes('failed') || event.type.includes('dropped');
          }

          // Support exact event type matching
          return event.type === filter;
        });
        if (!matchesFilter) {
          return false;
        }
      }

      // Debounce transport errors to prevent spam during tick loops
      if (event.type === 'transport-error') {
        var _transportEvent$error;
        var transportEvent = event;
        var errorKey = "".concat(event.type, "-").concat(transportEvent.messageId || 'unknown', "-").concat(((_transportEvent$error = transportEvent.error) === null || _transportEvent$error === void 0 ? void 0 : _transportEvent$error.message) || 'unknown');
        var lastLogged = this.recentErrors.get(errorKey) || 0;
        var now = Date.now();
        if (now - lastLogged < this.ERROR_DEBOUNCE_MS) {
          return false; // Skip this duplicate error
        }
        this.recentErrors.set(errorKey, now);
      }
      return true;
    }

    /**
     * Add event to history with size management.
     */
  }, {
    key: "addToHistory",
    value: function addToHistory(event) {
      this.eventHistory.push(event);

      // Trim history if it exceeds max size
      if (this.eventHistory.length > this.config.maxHistory) {
        this.eventHistory.shift();
      }
    }

    /**
     * Get color for a client ID.
     */
  }, {
    key: "getClientColor",
    value: function getClientColor(clientId) {
      if (!this.clientColors.has(clientId)) {
        var colorIndex = this.clientColors.size % this.colors.length;
        this.clientColors.set(clientId, this.colors[colorIndex]);
      }
      return this.clientColors.get(clientId);
    }

    /**
     * Get color for a command type.
     */
  }, {
    key: "getCommandColor",
    value: function getCommandColor(commandType) {
      if (!this.commandColors.has(commandType)) {
        var colorIndex = this.commandColors.size % this.colors.length;
        this.commandColors.set(commandType, this.colors[colorIndex]);
      }
      return this.commandColors.get(commandType);
    }

    /**
     * Get CSS style for event type.
     */
  }, {
    key: "getEventTypeStyle",
    value: function getEventTypeStyle(eventType) {
      var baseStyle = 'font-weight: bold; font-size: 12px;';
      if (eventType.includes('error') || eventType.includes('failed')) {
        return "".concat(baseStyle, " color: #FF6B6B;");
      }
      if (eventType.includes('completed') || eventType.includes('ready')) {
        return "".concat(baseStyle, " color: #4ECDC4;");
      }
      if (eventType.includes('created') || eventType.includes('sent')) {
        return "".concat(baseStyle, " color: #45B7D1;");
      }
      return "".concat(baseStyle, " color: #666;");
    }

    /**
     * Get CSS style for client ID.
     */
  }, {
    key: "getClientStyle",
    value: function getClientStyle(clientId) {
      return "color: ".concat(this.getClientColor(clientId), "; font-weight: bold;");
    }

    /**
     * Get CSS style for command type.
     */
  }, {
    key: "getCommandStyle",
    value: function getCommandStyle(commandType) {
      return "color: ".concat(this.getCommandColor(commandType), "; font-weight: bold;");
    }

    /**
     * Get CSS style for duration based on performance.
     */
  }, {
    key: "getDurationStyle",
    value: function getDurationStyle(duration) {
      if (duration < 10) {
        return 'color: #4ECDC4; font-weight: bold;';
      } // Fast
      if (duration < 100) {
        return 'color: #FFEAA7; font-weight: bold;';
      } // Medium
      return 'color: #FF6B6B; font-weight: bold;'; // Slow
    }

    /**
     * Get CSS style for queue size.
     */
  }, {
    key: "getQueueSizeStyle",
    value: function getQueueSizeStyle(size) {
      if (size === 0) {
        return 'color: #4ECDC4; font-weight: bold;';
      }
      if (size < 5) {
        return 'color: #FFEAA7; font-weight: bold;';
      }
      return 'color: #FF6B6B; font-weight: bold;';
    }

    /**
     * Format timestamp for display.
     */
  }, {
    key: "formatTimestamp",
    value: function formatTimestamp(timestamp) {
      var date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour12: false
      });
    }

    /**
     * Format duration in milliseconds.
     */
  }, {
    key: "formatDuration",
    value: function formatDuration(ms) {
      if (ms < 1) {
        return "".concat((ms * 1000).toFixed(0), "\u03BCs");
      }
      if (ms < 1000) {
        return "".concat(ms.toFixed(1), "ms");
      }
      return "".concat((ms / 1000).toFixed(2), "s");
    }
  }]);
}();

/**
 * Global debug consumer instance.
 */
var globalDebugConsumer = null;

/**
 * Get or create the global debug consumer.
 */
export function getDebugConsumer() {
  if (!globalDebugConsumer) {
    globalDebugConsumer = new BridgeDebugConsumer();
  }
  return globalDebugConsumer;
}

/**
 * Destroy the global debug consumer instance to prevent memory leaks.
 */
export function destroyGlobalDebugConsumer() {
  if (globalDebugConsumer) {
    globalDebugConsumer.disable();
    globalDebugConsumer = null;
  }
}